import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { Modal, ModalBody } from "reactstrap";
import {
  FaHeart,
  FaSearch,
  FaBed,
  FaBath,
  FaShareAlt,
  FaTrashAlt,
  FaCompass,
  FaPhoneAlt,
  FaBuilding,
  FaEye,
} from "react-icons/fa";
import { IoLocationSharp, IoSparkles, IoShieldCheckmark } from "react-icons/io5";
import { BiChevronRight } from "react-icons/bi";
import {
  getViewedProperties,
  removeViewedProperty,
  clearViewedProperties,
  SAMPLE_VIEWED_PROPERTIES,
} from "../../utils/activityUtils";
import {
  getShortlistedProperties,
  toggleShortlistProperty,
  isPropertyShortlisted,
} from "../../utils/shortlistUtils";
import PropertyDetailsModel from "../home/latestProperty/PropertyDetailsModel";
import "./ActivityPages.css";

const Viewed = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuth, loginData } = useSelector((store) => store.Athentication);

  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("recent");
  const [selectedPropForContact, setSelectedPropForContact] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [actionType, setActionType] = useState("contact");
  const [shortlistVersion, setShortlistVersion] = useState(0);

  // Load viewed properties
  useEffect(() => {
    const list = getViewedProperties();
    setItems(list);
  }, []);

  const shortlistCount = useMemo(() => {
    return getShortlistedProperties().length;
  }, [shortlistVersion]);

  // Handle heart toggle
  const handleToggleShortlist = (e, prop) => {
    e.preventDefault();
    e.stopPropagation();
    const added = toggleShortlistProperty(prop);
    setShortlistVersion((v) => v + 1);
    if (added) {
      toast.success("Saved to Shortlist!", { position: "top-center", autoClose: 1500 });
    } else {
      toast.info("Removed from Shortlist", { position: "top-center", autoClose: 1500 });
    }
  };

  // Handle remove from history
  const handleRemove = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const updated = removeViewedProperty(id);
    setItems(updated);
    toast.info("Removed from recently viewed", { position: "top-center", autoClose: 1400 });
  };

  // Handle clear all
  const handleClearAll = () => {
    if (window.confirm("Are you sure you want to clear your recently viewed history?")) {
      clearViewedProperties();
      setItems([]);
      toast.success("Recently viewed history cleared", { position: "top-center", autoClose: 1500 });
    }
  };

  // Load sample properties
  const handleLoadSample = () => {
    setItems(SAMPLE_VIEWED_PROPERTIES);
    try {
      localStorage.setItem("mgh_viewed_properties", JSON.stringify(SAMPLE_VIEWED_PROPERTIES));
    } catch (e) {}
    toast.success("Sample viewed properties loaded!", { position: "top-center", autoClose: 1500 });
  };

  // Open contact modal
  const handleOpenContact = (e, prop, type = "contact") => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedPropForContact(prop);
    setActionType(type);
    setContactModalOpen(true);
  };

  // Handle share
  const handleShare = async (e, prop) => {
    e.preventDefault();
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/property-info/${encodeURIComponent(
      (prop.apartment || prop.title || "property").toLowerCase().replace(/\s+/g, "-")
    )}/${prop._id || prop.propertyID}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: prop.apartment || "Property on EW Realty",
          text: `Check out ${prop.apartment || "this property"} on EW Realty`,
          url: shareUrl,
        });
        return;
      } catch (err) {}
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard!", { position: "top-center", autoClose: 1600 });
    } catch (err) {
      toast.info(`Share link: ${shareUrl}`, { position: "top-center", autoClose: 2500 });
    }
  };

  // Navigate to property details
  const handleCardClick = (prop) => {
    const slug = encodeURIComponent(
      (prop.apartment || prop.title || "property").toLowerCase().replace(/\s+/g, "-")
    );
    navigate(`/property-info/${slug}/${prop._id || prop.propertyID}`);
  };

  // Filter & Sort
  const filteredItems = useMemo(() => {
    let result = [...items];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          (p.apartment && p.apartment.toLowerCase().includes(q)) ||
          (p.locality && p.locality.toLowerCase().includes(q)) ||
          (p.city && p.city.toLowerCase().includes(q)) ||
          (p.propertyID && p.propertyID.toLowerCase().includes(q))
      );
    }

    if (selectedCategory !== "All") {
      result = result.filter((p) => {
        const cat = (p.category || "").toLowerCase();
        const sub = (p.subCategory || "").toLowerCase();
        const sup = (p.superCategory || "").toLowerCase();

        if (selectedCategory === "Apartments") return sub.includes("apartment") || sub.includes("flat");
        if (selectedCategory === "Villas") return sub.includes("villa") || sub.includes("house");
        if (selectedCategory === "Rent") return cat === "rent" || cat === "lease";
        if (selectedCategory === "Commercial") return sup === "commercial" || sub.includes("commercial");
        return true;
      });
    }

    if (sortBy === "price_low") {
      result.sort((a, b) => Number(a.expectedPrice || 0) - Number(b.expectedPrice || 0));
    } else if (sortBy === "price_high") {
      result.sort((a, b) => Number(b.expectedPrice || 0) - Number(a.expectedPrice || 0));
    }

    return result;
  }, [items, searchQuery, selectedCategory, sortBy]);

  return (
    <div className="activity-page">
      <Helmet>
        <title>Recently Viewed Properties | EW Realty</title>
        <meta
          name="description"
          content="Review your recently browsed and inspected properties on EW Realty."
        />
      </Helmet>

      <div className="activity-container">
        {/* Breadcrumb */}
        <div className="activity-breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <span className="current">Recently Viewed</span>
        </div>

        {/* Activity Navigation Tabs */}
        <div className="activity-tabs-nav">
          <Link to="/recentelysearched" className="activity-tab-item">
            <FaCompass />
            <span>Recent Searches</span>
          </Link>
          <div className="activity-tab-item active">
            <FaEye />
            <span>Recently Viewed</span>
            <span className="activity-tab-badge">{items.length}</span>
          </div>
          <Link to="/shortlisted" className="activity-tab-item">
            <FaHeart />
            <span>Shortlisted</span>
            <span className="activity-tab-badge">{shortlistCount}</span>
          </Link>
          <Link to="/contacted" className="activity-tab-item">
            <FaPhoneAlt />
            <span>Contacted</span>
          </Link>
        </div>

        {/* Header Summary Card */}
        <div className="activity-header-card">
          <div className="activity-header-left">
            <div className="activity-header-icon">
              <FaEye />
            </div>
            <div>
              <h1 className="activity-header-title">Recently Viewed Properties</h1>
              <p className="activity-header-subtitle">
                Revisit homes and commercial spaces you previously checked out.
              </p>
            </div>
          </div>
          <div className="activity-header-actions">
            {items.length > 0 && (
              <button type="button" className="activity-clear-btn" onClick={handleClearAll}>
                <FaTrashAlt />
                <span>Clear History</span>
              </button>
            )}
            <Link to="/allproperty" className="activity-explore-btn">
              <span>Explore More</span>
              <BiChevronRight size={18} />
            </Link>
          </div>
        </div>

        {/* Toolbar */}
        {items.length > 0 && (
          <div className="activity-toolbar">
            <div className="activity-search-box">
              <FaSearch />
              <input
                type="text"
                className="activity-search-input"
                placeholder="Search viewed by location, project..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="activity-filter-pills">
              {["All", "Apartments", "Villas", "Rent", "Commercial"].map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={`activity-pill ${selectedCategory === cat ? "active" : ""}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>

            <select
              className="activity-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="recent">Recently Viewed</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
            </select>
          </div>
        )}

        {/* Properties Grid */}
        {filteredItems.length > 0 ? (
          <div className="activity-grid">
            {filteredItems.map((prop, idx) => {
              const imageSrc =
                (prop.DeskImg && prop.DeskImg[0]) ||
                (prop.photos && prop.photos[0]) ||
                "/assets/images/hero_banner_new.png";
              const isFav = isPropertyShortlisted(prop);

              return (
                <article
                  key={prop._id || prop.propertyID || idx}
                  className="activity-card"
                  onClick={() => handleCardClick(prop)}
                >
                  {/* Media */}
                  <div className="activity-card-media">
                    <img
                      src={imageSrc}
                      alt={prop.apartment || prop.title || "Property"}
                      className="activity-card-img"
                      loading="lazy"
                    />

                    <div className="activity-badge-verified">
                      <IoShieldCheckmark />
                      <span>VERIFIED</span>
                    </div>

                    <div className="activity-badge-category">
                      {prop.category || "FOR SALE"}
                    </div>

                    {prop.viewedAt && (
                      <div className="activity-card-time-badge">
                        {prop.viewedAt}
                      </div>
                    )}

                    <button
                      type="button"
                      className={`activity-card-fav-btn ${isFav ? "active" : ""}`}
                      onClick={(e) => handleToggleShortlist(e, prop)}
                      title={isFav ? "Remove from shortlist" : "Add to shortlist"}
                    >
                      <FaHeart />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="activity-card-body">
                    <div className="activity-card-price-row">
                      <span className="activity-card-price">
                        ₹ {prop.visiblePrice || "On Request"}
                      </span>
                      {prop.noofBedrooms && (
                        <span className="activity-card-bhk">
                          {prop.noofBedrooms} BHK
                        </span>
                      )}
                    </div>

                    <h3 className="activity-card-title">
                      {prop.apartment || prop.title || "Premium Property"}
                    </h3>

                    <div className="activity-card-location">
                      <IoLocationSharp />
                      <span>{prop.locality || "Prime Locality"}, {prop.city || "Hyderabad"}</span>
                    </div>

                    {/* Specs Row */}
                    <div className="activity-specs-row">
                      <div className="activity-spec-item" title="Carpet Area">
                        <FaBuilding />
                        <span>{prop.carpetArea ? `${prop.carpetArea} sq.ft` : "Spacious"}</span>
                      </div>
                      <span className="activity-spec-dot">•</span>
                      <div className="activity-spec-item" title="Bedrooms">
                        <FaBed />
                        <span>{prop.noofBedrooms ? `${prop.noofBedrooms} Beds` : "3 Beds"}</span>
                      </div>
                      <span className="activity-spec-dot hide-on-mobile">•</span>
                      <div className="activity-spec-item hide-on-mobile" title="Bathrooms">
                        <FaBath />
                        <span>{prop.noofBathrooms ? `${prop.noofBathrooms} Baths` : "3 Baths"}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="activity-card-actions">
                      <button
                        type="button"
                        className="activity-view-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardClick(prop);
                        }}
                      >
                        <span>View</span>
                      </button>

                      <button
                        type="button"
                        className="activity-contact-btn"
                        onClick={(e) => handleOpenContact(e, prop, "phone")}
                        title="Contact Advertiser"
                      >
                        <FaPhoneAlt />
                        <span className="btn-text-full">Contact</span>
                      </button>

                      <button
                        type="button"
                        className="activity-share-btn"
                        onClick={(e) => handleShare(e, prop)}
                        title="Share Property"
                      >
                        <FaShareAlt />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          /* Empty State */
          <div className="activity-empty-card">
            <div className="activity-empty-graphic">
              <FaEye />
            </div>
            <h2 className="activity-empty-title">No Recently Viewed Properties</h2>
            <p className="activity-empty-desc">
              Properties you explore across EW Realty will automatically appear here so you can easily compare and revisit them.
            </p>
            <div className="activity-empty-actions">
              <Link to="/allproperty" className="activity-explore-btn">
                <FaCompass />
                <span>Explore Properties</span>
              </Link>
              <button
                type="button"
                className="activity-sample-btn"
                onClick={handleLoadSample}
              >
                <IoSparkles />
                <span>Show Sample Viewed</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details & Contact Modal (Centered Dead-Center) */}
      {contactModalOpen && selectedPropForContact && (
        <Modal
          isOpen={contactModalOpen}
          toggle={() => setContactModalOpen(!contactModalOpen)}
          className="modalmainnew"
          centered
        >
          <div className="model-wrappernew">
            <ModalBody style={{ padding: 0 }}>
              <PropertyDetailsModel
                closePropdet={() => setContactModalOpen(false)}
                propertyprop={selectedPropForContact}
                actionType={actionType}
                changeView={() => {}}
              />
            </ModalBody>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Viewed;